import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import LoadingScreen from '../components/Layout/LoadingScreen';

/**
 * Dedicated logout page that handles the logout process and redirects to login
 */
export default function Logout() {
  const [isLoggingOut, setIsLoggingOut] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const performLogout = async () => {
      try {
        // Clear all storage
        localStorage.clear();
        sessionStorage.clear();
        
        // Force clear all cookies related to authentication
        document.cookie.split(';').forEach(cookie => {
          const [name] = cookie.trim().split('=');
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });
        
        // Sign out from Supabase with global scope
        await supabase.auth.signOut({ scope: 'global' });
        
        // Add a delay to ensure everything is cleared
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Set logging out to false to trigger redirect
        setIsLoggingOut(false);
      } catch (err) {
        console.error('Logout error:', err);
        setError('Failed to log out. Please try again.');
        
        // Even if there's an error, still redirect after a delay
        setTimeout(() => {
          setIsLoggingOut(false);
        }, 2000);
      }
    };

    performLogout();
  }, []);

  // If there was an error, show it briefly before redirecting
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
        <div className="p-8 bg-white rounded-lg shadow-md max-w-md w-full">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Logout Error</h1>
          <p className="text-gray-700 mb-4">{error}</p>
          <p className="text-gray-500">Redirecting to login page...</p>
        </div>
      </div>
    );
  }

  // While logging out, show a loading screen
  if (isLoggingOut) {
    return <LoadingScreen message="Signing out..." />;
  }

  // Once logout is complete, redirect to login page
  return <Navigate to="/login" replace />;
}
